import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useHeaderGeneration } from '@/hooks/text-generation';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { researchClient } from '@/integrations/clients';
import { IntegrationError } from '@/integrations/shared';
import type { ResearchRequest } from '@/integrations/perplexity';

interface GenerateResearchParams {
  researchType?: string; // Now optional
  researchPrompt: string;
  timeframe?: string;
  country?: string;
  sourceType?: string;
  selectedFilters?: Record<string, string>;
  clearFilters?: () => void;
}

export const useResearchGeneration = () => {
  const queryClient = useQueryClient();
  const { generateHeader } = useHeaderGeneration();
  const { executeAIOperation } = useAIOperationWrapper();

  return useMutation({
    mutationFn: async (params: GenerateResearchParams): Promise<any> => {
      return executeAIOperation(
        async () => {
          console.log('Starting research generation with optimized system:', params);
          
          // Check if user is authenticated
          const { data: { user }, error: authError } = await supabase.auth.getUser();
          
          if (authError || !user) {
            throw new Error('You must be logged in to generate research');
          }

          // Map frontend research type names to backend-compatible format if needed
          const researchTypeMapping: Record<string, string> = {
            'Technology & Innovation': 'Technology & Innovation',
            'Business & Professional': 'Business & Professional', 
            'Content & Creative': 'Content & Creative',
            'Personal Development': 'Personal Development',
            'Academic & Learning': 'Academic & Learning',
            'Current Events & News': 'Current Events & News'
          };

          // Use the research type as-is or default to "General Research"
          const effectiveResearchType = params.researchType 
            ? (researchTypeMapping[params.researchType] || params.researchType)
            : 'General Research';

          console.log('Using research type:', effectiveResearchType);

          // Build research request for the new client
          const researchRequest: ResearchRequest = {
            researchType: effectiveResearchType,
            researchPrompt: params.researchPrompt,
            timeframe: params.timeframe,
            country: params.country,
            sourceType: params.sourceType,
            selectedFilters: params.selectedFilters,
          };

          // Call the research client instead of direct edge function
          let researchData;
          try {
            researchData = await researchClient.generateResearch(researchRequest);
          } catch (error) {
            console.error('Research generation error:', error);
            const errorMessage = error instanceof IntegrationError 
              ? error.message 
              : `Research generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
            throw new Error(errorMessage);
          }

          if (!researchData) {
            throw new Error('No research data returned from function');
          }

          console.log('Research data received:', researchData);

          // Generate a header using the header generation system
          let title: string;
          try {
            console.log('Generating header for research content...');
            const headerResult = await generateHeader({
              content: researchData.content || '',
              originalPrompt: params.researchPrompt,
              context: `Research Type: ${effectiveResearchType}${params.timeframe ? `, Timeframe: ${params.timeframe}` : ''}${params.country ? `, Country: ${params.country}` : ''}`,
            });

            title = headerResult.header;
            console.log('Generated header:', title);
          } catch (headerError) {
            console.error('Header generation failed, using fallback:', headerError);
            // Fallback to original prompt if header generation fails
            title = params.researchPrompt.substring(0, 100);
          }

          // Build research parameters object with optimized category system
          const researchParameters = {
            research_type: effectiveResearchType,
            timeframe: params.timeframe || null,
            timeframe_label: researchData.timeframeLabel || null,
            date_range_start: researchData.dateRangeStart || null,
            date_range_end: researchData.dateRangeEnd || null,
            country: params.country || null,
            source_type: params.sourceType || null,
            type_specific_filters: params.selectedFilters || {}
          };

          console.log('Research parameters object:', researchParameters);

          // Save the research to the database including the system prompt used
          const { data: savedResearch, error: saveError } = await supabase
            .from('research_library')
            .insert({
              user_id: user.id,
              title,
              content: researchData.content,
              research_type: effectiveResearchType,
              original_prompt: params.researchPrompt,
              metadata: researchData.metadata || {},
              sources: researchData.sources || [],
              related_questions: researchData.related_questions || [],
              usage: researchData.usage || {},
              research_parameters: researchParameters,
              system_prompt: researchData.system_prompt || null, // Save the system prompt that was used
            })
            .select()
            .single();

          if (saveError) {
            console.error('Error saving research:', saveError);
            throw new Error(`Failed to save research: ${saveError.message}`);
          }

          console.log('Research saved successfully with system prompt:', savedResearch);
          return savedResearch;
        },
        {
          immediate: true, // Research generation needs immediate feedback
          errorPrefix: 'Research generation failed',
          debug: true // Enable debug logging for research generation
        }
      );
    },
    onSuccess: (data, variables) => {
      console.log('Research generation successful, invalidating list queries');
      toast.success('Research generated successfully!');
      
      // Clear filters AFTER successful save
      if (variables.clearFilters) {
        console.log('Clearing filters after successful research generation');
        variables.clearFilters();
      }
      
      // Targeted invalidation - only invalidate list queries
      queryClient.invalidateQueries({ 
        queryKey: ['research-library-list'],
        exact: false,
        refetchType: 'active'
      });
      
      console.log('Query invalidation completed');
    },
    onError: (error) => {
      console.error('Research generation error:', error);
      toast.error(`Failed to generate research: ${error.message}`);
    },
  });
};
